import { c as _c } from "react/compiler-runtime";
import type { LucideIcon } from "lucide-react";
import { Boxes, Building2, Calendar, Gem, Hash, Package, Ruler, Square } from "lucide-react";
import { t, type SupportedLocale } from "@/lib/i18n";
import type { Product } from "@/data/products";
import styles from "@/styles/Home.module.css";
type ProductFeaturesListProps = {
  product: Product;
  locale: SupportedLocale;
};
type FeatureStat = {
  key: string;
  icon: LucideIcon;
  value: string;
  label: string;
};
const iconProps = {
  size: 38,
  strokeWidth: 1.5,
  absoluteStrokeWidth: false
} as const;
function getProductSize(product: Product): string | undefined {
  if (product.productSize?.trim()) return product.productSize.trim();
  if (product.blockSize && product.blockSize !== "normal") return product.blockSize;
  return undefined;
}
export function ProductFeaturesList(t0) {
  const $ = _c(58);
  const {
    product,
    locale
  } = t0;
  let t1;
  if ($[0] !== product) {
    t1 = getProductSize(product);
    $[0] = product;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const productSize = t1;
  const t2 = product.numbering;
  let t3;
  if ($[2] !== locale) {
    t3 = t(locale, "product.statCatalogNumber");
    $[2] = locale;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== product.numbering || $[5] !== t3) {
    t4 = {
      key: "catalogNumber",
      icon: Hash,
      value: t2,
      label: t3
    };
    $[4] = product.numbering;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== locale) {
    t5 = t(locale, "product.statManufacturer");
    $[7] = locale;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = {
      key: "manufacturer",
      icon: Building2,
      value: "12GO",
      label: t5
    };
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = String(product.pieces);
  let t8;
  if ($[11] !== locale) {
    t8 = t(locale, "product.statPieces");
    $[11] = locale;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t7 || $[14] !== t8) {
    t9 = {
      key: "pieces",
      icon: Boxes,
      value: t7,
      label: t8
    };
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let stats;
  if ($[16] !== locale || $[17] !== product.packageSize || $[18] !== product.recommendAge || $[19] !== product.scale || $[20] !== productSize || $[21] !== t4 || $[22] !== t6 || $[23] !== t9) {
    stats = [t4, t6, t9];
    if (product.scale?.trim()) {
      let t10;
      if ($[25] !== product.scale) {
        t10 = product.scale.trim();
        $[25] = product.scale;
        $[26] = t10;
      } else {
        t10 = $[26];
      }
      let t11;
      if ($[27] !== locale) {
        t11 = t(locale, "product.statScale");
        $[27] = locale;
        $[28] = t11;
      } else {
        t11 = $[28];
      }
      let t12;
      if ($[29] !== t10 || $[30] !== t11) {
        t12 = {
          key: "scale",
          icon: Ruler,
          value: t10,
          label: t11
        };
        $[29] = t10;
        $[30] = t11;
        $[31] = t12;
      } else {
        t12 = $[31];
      }
      stats.push(t12);
    }
    if (productSize) {
      let t10;
      if ($[32] !== locale) {
        t10 = t(locale, "product.statDimensions");
        $[32] = locale;
        $[33] = t10;
      } else {
        t10 = $[33];
      }
      let t11;
      if ($[34] !== productSize || $[35] !== t10) {
        t11 = {
          key: "productSize",
          icon: Square,
          value: productSize,
          label: t10
        };
        $[34] = productSize;
        $[35] = t10;
        $[36] = t11;
      } else {
        t11 = $[36];
      }
      stats.push(t11);
    }
    if (product.packageSize?.trim()) {
      let t10;
      if ($[37] !== product.packageSize) {
        t10 = product.packageSize.trim();
        $[37] = product.packageSize;
        $[38] = t10;
      } else {
        t10 = $[38];
      }
      let t11;
      if ($[39] !== locale) {
        t11 = t(locale, "product.statPackageSize");
        $[39] = locale;
        $[40] = t11;
      } else {
        t11 = $[40];
      }
      let t12;
      if ($[41] !== t10 || $[42] !== t11) {
        t12 = {
          key: "packageSize",
          icon: Package,
          value: t10,
          label: t11
        };
        $[41] = t10;
        $[42] = t11;
        $[43] = t12;
      } else {
        t12 = $[43];
      }
      stats.push(t12);
    }
    const t10 = product.recommendAge;
    let t11;
    if ($[44] !== locale) {
      t11 = t(locale, "product.statAge");
      $[44] = locale;
      $[45] = t11;
    } else {
      t11 = $[45];
    }
    let t12;
    if ($[46] !== product.recommendAge || $[47] !== t11) {
      t12 = {
        key: "age",
        icon: Calendar,
        value: t10,
        label: t11
      };
      $[46] = product.recommendAge;
      $[47] = t11;
      $[48] = t12;
    } else {
      t12 = $[48];
    }
    let t13;
    if ($[49] !== locale) {
      t13 = t(locale, "product.statMaterialAbs");
      $[49] = locale;
      $[50] = t13;
    } else {
      t13 = $[50];
    }
    let t14;
    if ($[51] !== locale) {
      t14 = t(locale, "product.statMaterial");
      $[51] = locale;
      $[52] = t14;
    } else {
      t14 = $[52];
    }
    let t15;
    if ($[53] !== t13 || $[54] !== t14) {
      t15 = {
        key: "material",
        icon: Gem,
        value: t13,
        label: t14
      };
      $[53] = t13;
      $[54] = t14;
      $[55] = t15;
    } else {
      t15 = $[55];
    }
    stats.push(t12, t15);
    $[16] = locale;
    $[17] = product.packageSize;
    $[18] = product.recommendAge;
    $[19] = product.scale;
    $[20] = productSize;
    $[21] = t4;
    $[22] = t6;
    $[23] = t9;
    $[24] = stats;
  } else {
    stats = $[24];
  }
  let t10;
  if ($[56] !== stats) {
    t10 = <div className={styles.productFeaturesBlock}><ul className={styles.productFeaturesStats}>{stats.map(_temp)}</ul></div>;
    $[56] = stats;
    $[57] = t10;
  } else {
    t10 = $[57];
  }
  return t10;
}
function _temp(stat) {
  const Icon = stat.icon;
  return <li key={stat.key} className={styles.productFeatureStat}><span className={styles.productFeatureIcon} aria-hidden="true"><Icon {...iconProps} /></span><span className={styles.productFeatureText}><span className={styles.productFeatureValue}>{stat.value}</span><span className={styles.productFeatureLabel}>{stat.label}</span></span></li>;
}